import api from './api'

// Belge türü → uç yolu parçası. Proforma ve Sipariş Teyidi aynı motoru kullanır
// ama AYRI uçlardan geçer: URL ne gönderdiğini söylesin (teyit ≠ proforma).
const DOC_SEG = { proforma: 'proforma', order_confirm: 'order-confirm' }
const seg = (kind) => DOC_SEG[kind] || DOC_SEG.proforma

export const salesService = {
  // Proforma Fatura PDF — items: [10,20] (boş = tüm kalemler). quoteId verilirse proforma
  // doğrudan teklifden üretilir (DEV'de canias siparişi yokken de çalışır).
  getOrderProforma: async (docnum, { company, doctype = 'MS', items, quoteId, kind, installment } = {}) => {
    const res = await api.get(`/sales/orders/${docnum}/${seg(kind)}`, {
      params: {
        company, doctype,
        ...(items && items.length ? { items: items.join(',') } : {}),
        ...(quoteId ? { quote_id: quoteId } : {}),
        ...(installment ? { installment } : {}),
      },
      responseType: 'blob',
    })
    return res.data
  },

  // Kalem değişiklikleri + gerekçeleri (onaylayan da görebilsin diye rol kapısı yok).
  getItemChanges: async (docnum, { company, doctype } = {}) => {
    const res = await api.get(`/sales/orders/${encodeURIComponent(docnum)}/item-changes`, {
      params: { company, doctype },
    })
    return res.data
  },

  // Belgenin kaynak teklifine göre farkı (silinen/eklenen/değişen kalem).
  // canias'ta silinen kalem iz bırakmadığı için bu bilgi başka yerden okunamaz.
  getQuoteDiff: async (docnum, { company, doctype = 'AS' } = {}) => {
    const res = await api.get(`/sales/orders/${encodeURIComponent(docnum)}/quote-diff`, {
      params: { company, doctype },
    })
    return res.data
  },

  // Proforma mail compose önizlemesi — teklif 'Mail At' ekranıyla birebir şekil
  // (accounts, from_account_id, to, cc, subject, html_body, attachment_name, mode, can_send).
  // from_account_id → kutu değişince yeniden çözer. Dönüş {data:{...}} (composer bu şekli bekler).
  getProformaEmailPreview: async (docnum, { company, doctype = 'MS', quoteId, kind, installment } = {}, fromAccountId) => {
    const res = await api.get(`/sales/orders/${docnum}/${seg(kind)}/email-context`, {
      params: {
        company, doctype,
        ...(fromAccountId ? { from_account_id: fromAccountId } : {}),
        ...(quoteId ? { quote_id: quoteId } : {}),
        ...(installment ? { installment } : {}),
      },
    })
    return res.data
  },

  // Proformayı PDF ekiyle mail at — payload composer'dan:
  // {subject, to, cc, bcc, html_body, attachments, from_account_id} (+ items/quote_id query'den)
  sendProformaEmail: async (docnum, { company, doctype = 'MS', items, quoteId, kind, installment } = {}, payload = {}) => {
    const res = await api.post(`/sales/orders/${docnum}/${seg(kind)}/email`,
      { ...payload, ...(items && items.length ? { items } : {}), ...(quoteId ? { quote_id: quoteId } : {}), ...(installment ? { installment } : {}) },
      { params: { company, doctype } })
    return res.data
  },

  // Proforma PDF'ini yeni sekmede aç (JWT ile blob) — composer ek önizlemesi
  openProformaPdf: async (docnum, { company, doctype = 'MS', items, quoteId, kind, installment } = {}) => {
    const res = await api.get(`/sales/orders/${docnum}/${seg(kind)}`, {
      params: {
        company, doctype,
        ...(items && items.length ? { items: items.join(',') } : {}),
        ...(quoteId ? { quote_id: quoteId } : {}),
        ...(installment ? { installment } : {}),
      },
      responseType: 'blob',
    })
    const url = URL.createObjectURL(res.data)
    window.open(url, '_blank', 'noopener')
    setTimeout(() => URL.revokeObjectURL(url), 60000)
  },

  getSaldeptNames: async (params = {}) => {
    const response = await api.get('/sales/saldept-names', { params })
    return response.data
  },

  // opts.signal → arama sırasında geçersizleşen istek iptal edilir; canias sorgusu
  // pahalı olduğu için iptal edilmeyen istekler sunucuda birikiyordu.
  getOpenOrders: async (company = '01', params = {}, opts = {}) => {
    const response = await api.get('/sales/open-orders',
      { params: { company, ...params }, signal: opts.signal })
    return response.data
  },

  getOpenOrderDetail: async (company, material, params = {}) => {
    const response = await api.get(`/sales/open-orders/${encodeURIComponent(material)}/detail`, {
      params: { company, ...params },
    })
    return response.data
  },

  getOpenOrderCustomerDetail: async (company, customer, params = {}) => {
    const response = await api.get(`/sales/open-orders-customer/${encodeURIComponent(customer)}/detail`, {
      params: { company, ...params },
    })
    return response.data
  },

  getOpenOrderDocumentDetail: async (company, doctype, docnum, params = {}) => {
    const response = await api.get(`/sales/open-orders-document/${encodeURIComponent(doctype)}/${encodeURIComponent(docnum)}/detail`, {
      params: { company, ...params },
    })
    return response.data
  },

  // Partner stok eşleştirme — kod+miktar listesi → FIFO açık sipariş tahsisi
  partnerMatch: async (company, body, params = {}) => {
    const response = await api.post('/sales/open-orders/partner-match', body, {
      params: { company, ...params },
    })
    return response.data
  },

  getWinLoss: async (company = '01', params = {}) => {
    const response = await api.get('/sales/win-loss', { params: { company, ...params } })
    return response.data
  },

  getSummary: async (company = '01', params = {}) => {
    const response = await api.get('/sales/summary', {
      params: { company, ...params },
    })
    return response.data
  },

  getOverview: async (company = '01', params = {}) => {
    const response = await api.get('/sales/overview', {
      params: { company, ...params },
    })
    return response.data
  },

  getProductInvoices: async (company = '01', params = {}) => {
    const response = await api.get('/sales/by-product/invoices', {
      params: { company, ...params },
    })
    return response.data
  },

  getByPerson: async (company = '01', params = {}) => {
    const response = await api.get('/sales/by-person', {
      params: { company, ...params },
    })
    return response.data
  },

  getByPersonDetail: async (saldept, company = '01', params = {}) => {
    const response = await api.get(`/sales/by-person/${saldept}`, {
      params: { company, ...params },
    })
    return response.data
  },

  getByProduct: async (company = '01', params = {}) => {
    const response = await api.get('/sales/by-product', {
      params: { company, ...params },
    })
    return response.data
  },

  getCustomers: async (company = '01', params = {}) => {
    const response = await api.get('/sales/customers', {
      params: { company, ...params },
    })
    return response.data
  },

  getCustomerDetail: async (customer, company = '01', params = {}) => {
    const response = await api.get(`/sales/customers/${customer}`, {
      params: { company, ...params },
    })
    return response.data
  },

  getOrders: async (company = '01', params = {}) => {
    const response = await api.get('/sales/orders', {
      params: { company, ...params },
    })
    return response.data
  },

  // Sütun hunisi değerleri — TÜM veri üzerinden (açık sayfa değil)
  orderFacets: async (company = '01', params = {}) => (
    await api.get('/sales/orders/facets', { params: { company, ...params } })
  ).data,

  // Müşteri sipariş no listesi — açılır filtre için (canias PRINTEDORDNUM, canlı)
  getCustomerOrderNumbers: async (company = '01', params = {}) => {
    const response = await api.get('/sales/orders/customer-order-numbers', {
      params: { company, ...params },
    })
    return response.data
  },

  getQuotations: async (company = '01', params = {}) => {
    const response = await api.get('/sales/quotations', {
      params: { company, ...params },
    })
    return response.data
  },

  getOrderDetail: async (docnum, company = '01', doctype = null) => {
    const response = await api.get(`/sales/orders/${docnum}`, {
      params: { company, ...(doctype && { doctype }) },
    })
    return response.data
  },

  // ── Sipariş Tedariği (stok rezervasyonu + siparişe bağlı satınalma) ──
  getOrderSupply: async (docnum, company = '01', doctype = 'AS') => {
    const response = await api.get(`/sales/orders/${docnum}/supply`, { params: { company, doctype } })
    return response.data
  },

  reserveOrderStock: async (docnum, company, doctype, lines) => {
    const response = await api.post(`/sales/orders/${docnum}/reserve`, { lines },
      { params: { company, doctype } })
    return response.data
  },

  // Bağsız açık O1'i siparişe havuzla bağlama / bağı kaldırma (IASRESERVATIONPOOL)
  bindOrderOpenPurchases: async (docnum, company, doctype, lines) =>
    (await api.post(`/sales/orders/${docnum}/bind-open-orders`, { lines }, { params: { company, doctype } })).data,
  unbindOrderOpenPurchases: async (docnum, company, doctype, resernums) =>
    (await api.post(`/sales/orders/${docnum}/bind-open-orders/cancel`, { resernums }, { params: { company, doctype } })).data,

  cancelOrderReservations: async (docnum, company, doctype, resernums) => {
    const response = await api.post(`/sales/orders/${docnum}/reserve/cancel`, { resernums },
      { params: { company, doctype } })
    return response.data
  },

  // İrsaliye (I1) — sipariş ekranından. DEV'de test numarası; canias yazımı trace sonrası açılır.
  getDeliveryNotePrep: async (docnum, company, doctype = 'AS') =>
    (await api.get(`/sales/orders/${encodeURIComponent(docnum)}/delivery-note/prepare`, { params: { company, doctype } })).data,
  createDeliveryNote: async (docnum, company, doctype, body) =>
    (await api.post(`/sales/orders/${encodeURIComponent(docnum)}/delivery-note`, body, { params: { company, doctype } })).data,

  // submit=true → taslak ekranına düşmeden numara alınır ve belge onaya gider (PROD);
  // DEV'de canias'a yazılmaz, önizleme döner. change_note: iskonto sapmasında zorunlu gerekçe.
  createOrderPurchaseDraft: async (docnum, company, doctype, lines, ordertype = 'OS', opts = {}) => {
    const response = await api.post(`/sales/orders/${docnum}/purchase-draft`,
      { lines, ordertype, ...(opts.submit ? { submit: true } : {}), ...(opts.changeNote ? { change_note: opts.changeNote } : {}), ...(opts.currency ? { currency: opts.currency } : {}),
        ...(opts.vendorTerms ? { vendor_terms: opts.vendorTerms } : {}) },
      { params: { company, doctype } })
    return response.data
  },

  getOrderStockCost: async (docnum, company = '01', materials = [], quantities = []) => {
    const response = await api.get(`/sales/orders/${docnum}/stock-cost`, {
      params: { company, materials: materials.join(','), quantities: quantities.join(',') },
    })
    return response.data
  },

  getOrderItemsWithStock: async (docnum, company, doctype) => {
    const response = await api.get(`/sales/orders/${docnum}/items-with-stock`, {
      params: { company, doctype },
    })
    return response.data
  },

  getProfitTrend: async (company = '01', params = {}) => {
    const response = await api.get('/analysis/trends/profit', { params: { company, ...params }, timeout: 300000 })
    return response.data
  },
  getOrdersRealProfitTotal: async (company = '01', params = {}) => {
    const response = await api.get('/sales/orders/real-profit-total', { params: { company, ...params }, timeout: 180000 })
    return response.data
  },
  getOrdersRealProfit: async (company = '01', docnums = [], doctypes = []) => {
    const response = await api.post('/sales/orders/real-profit', { docnums, doctypes }, {
      params: { company },
    })
    return response.data
  },

  getApprovals: async (company = '01', params = {}) => {
    const response = await api.get('/approval/sales', {
      params: { company, ...params },
    })
    return response.data
  },

  getMap: async (company = '01', params = {}) => {
    const response = await api.get('/sales/map', {
      params: { company, ...params },
    })
    return response.data
  },

  approveOrder: async (docnum, company = '01', data = {}) => {
    const response = await api.post(`/approval/sales/${docnum}`, data, {
      params: { company },
    })
    return response.data
  },

  rejectOrder: async (docnum, company = '01', reason = '') => {
    const response = await api.post(`/sales/orders/${docnum}/reject`, { reason }, {
      params: { company },
    })
    return response.data
  },

  // ── Teklif Onay Sistemi ──────────────────────────────────
  getQuoteApprovalSummary: async (company = '01', params = {}) => {
    const response = await api.get('/sales/quote-approvals/summary', {
      params: { company, ...params },
    })
    return response.data
  },

  getQuoteApprovals: async (company = '01', params = {}) => {
    const response = await api.get('/sales/quote-approvals', {
      params: { company, ...params },
    })
    return response.data
  },

  getQuoteApprovalDetail: async (doctype, docnum, company = '01') => {
    const response = await api.get(`/sales/quote-approvals/${doctype}/${docnum}`, {
      params: { company },
    })
    return response.data
  },

  getQuoteProfitByCategory: async (params = {}) => {
    const response = await api.get('/sales/quotes/profit-by-category', { params })
    return response.data
  },

  getQuoteResignHistory: async (doctype, docnum, company = '01') => {
    const response = await api.get(`/sales/quote-approvals/${doctype}/${docnum}/resign-history`, {
      params: { company },
    })
    return response.data
  },

  approveQuote: async (doctype, docnum, company = '01', data = {}) => {
    const response = await api.post(`/sales/quote-approvals/${doctype}/${docnum}`, data, {
      params: { company },
    })
    return response.data
  },

  rejectQuote: async (doctype, docnum, company = '01', data = {}) => {
    const response = await api.post(`/sales/quote-approvals/${doctype}/${docnum}/reject`, data, {
      params: { company },
    })
    return response.data
  },

  bulkRejectOldQuotes: async (company = null, data = {}) => {
    const params = company ? { company } : {}
    const response = await api.post('/sales/quote-approvals/bulk-reject-old', data, { params })
    return response.data
  },

  getRealtimeToday: async (company = '01', params = {}) => {
    const response = await api.get('/sales/realtime/today', {
      params: { company, ...params },
    })
    return response.data
  },

  getTargets: async (company = '01', params = {}) => {
    const response = await api.get('/sales/targets', {
      params: { company, ...params },
    })
    return response.data
  },

  getTargetActuals: async (company = '01', params = {}) => {
    const response = await api.get('/sales/targets/actuals', {
      params: { company, ...params },
    })
    return response.data
  },

  getTargetDetail: async (username, company = '01', params = {}) => {
    const response = await api.get(`/sales/targets/${username}`, {
      params: { company, ...params },
    })
    return response.data
  },

  getByCity: async (company = '01', params = {}) => {
    const response = await api.get('/sales/by-city', {
      params: { company, ...params },
    })
    return response.data
  },

  // Haritada şehre tıklayınca: o şehirdeki firmalar (aynı süzgeç penceresi)
  getCityCustomers: async (company = '01', params = {}, signal) => {
    const response = await api.get('/sales/by-city/customers', {
      params: { company, ...params }, signal,
    })
    return response.data
  },

  getInvoiceProfitLoss: async (company = '01', params = {}) => {
    const response = await api.get('/sales/invoices/profit-loss', {
      params: { company, ...params },
    })
    return response.data
  },

  getInvoiceItems: async (doctype, docnum, company = '01') => {
    const response = await api.get(`/sales/invoices/${doctype}/${docnum}/items`, {
      params: { company },
    })
    return response.data
  },

  listDeliveries: async (company = '01', params = {}) => (await api.get('/sales/deliveries', { params: { company, ...params } })).data,
  // Sütun hunisi değerleri — TÜM veri üzerinden (açık sayfa değil)
  deliveryFacets: async (company = '01', params = {}) => (await api.get('/sales/deliveries/facets', { params: { company, ...params } })).data,
  getDeliveryDetail: async (docnum, company = '01', doctype = null) =>
    (await api.get(`/sales/deliveries/${docnum}`, { params: { company, ...(doctype && { doctype }) } })).data,

  listInvoices: async (company = '01', params = {}) => (await api.get('/sales/invoices', { params: { company, ...params } })).data,
  invoiceFacets: async (company = '01', params = {}) => (await api.get('/sales/invoices/facets', { params: { company, ...params } })).data,
  getInvoiceDetail: async (docnum, company = '01', doctype = null) =>
    (await api.get(`/sales/invoices/${docnum}`, { params: { company, ...(doctype && { doctype }) } })).data,
  // GİB e-belgesi (karekodlu) — PDF (PROD) ya da korumalı görüntülenecek HTML (DEV)
  fetchInvoiceEDocument: async (docnum, company, doctype) => {
    const r = await api.get(`/sales/invoices/${docnum}/e-document`, {
      params: { company, doctype }, responseType: 'blob', timeout: 120000,
    })
    const type = r.data?.type || r.headers?.['content-type'] || ''
    const number = r.headers?.['x-vertex-edoc-number'] || `${doctype}-${docnum}`
    return { blob: r.data, isPdf: type.includes('pdf'), number }
  },

  // Müşteri sipariş no (canias PRINTEDORDNUM) — doğrudan canias'a yazar.
  // VERTEX'te kopya tutulmaz; tek doğru kaynak canias.
  setCustomerOrderNo: async (docnum, { company = '01', doctype, value } = {}) =>
    (await api.patch(`/sales/orders/${encodeURIComponent(docnum)}/customer-order-no`,
      { value }, { params: { company, doctype } })).data,

  // ── Sipariş carisini değiştirme (canias'a yazar) ──
  // Önce check → engel/uyarı listesi + önizleme; sonra changeOrderCustomer.
  // Ön kontrol sunucuda TEKRAR koşar, bu çağrı yalnız kullanıcıya göstermek için.
  checkOrderCustomerChange: async (docnum, { company = '01', doctype, target } = {}) =>
    (await api.get(`/sales/orders/${encodeURIComponent(docnum)}/customer-change/check`,
      { params: { company, doctype, target } })).data,

  changeOrderCustomer: async (docnum, { company = '01', doctype, target } = {}) =>
    (await api.patch(`/sales/orders/${encodeURIComponent(docnum)}/customer`,
      { target }, { params: { company, doctype } })).data,

  // Hedef cari seçici — canias IASCUSTOMER (satışta kullanılabilir + kilitli olmayan).
  // Satınalma router'ındaki mevcut uç; ikinci bir arama ucu açılmadı.
  searchCustomers: async ({ company = '01', q } = {}) =>
    (await api.get('/purchase/customer-search', { params: { company, q } })).data,

  // Belge akışı — canias SALT01 "Detaylı Belge Akışı" karşılığı.
  // Zincirin neresinden sorulursa sorulsun AYNI tam ağaç döner:
  // teklifte ileri, siparişte hem ileri hem geri.
  getDocumentFlow: async (company = '01', { doctype, docnum, quoteId } = {}) =>
    (await api.get('/sales/document-flow', {
      params: {
        company,
        ...(quoteId ? { quote_id: quoteId } : { doctype, docnum }),
      },
    })).data,

  // ── Sipariş kalemi düzenleme (canias IASSALITEM) ──────────────────────────
  // Kilit KALEM seviyesindedir ve belge zincirinden gelir; sunucu ayrıca doğrular.
  getOrderItemLocks: async (docnum, company, doctype) =>
    (await api.get(`/sales/orders/${encodeURIComponent(docnum)}/item-locks`,
      { params: { company, doctype } })).data,

  addOrderItem: async (docnum, company, doctype, payload) =>
    (await api.post(`/sales/orders/${encodeURIComponent(docnum)}/items`,
      payload, { params: { company, doctype } })).data,

  // Kalemin canias'taki GÜNCEL alanları — düzenleme paneli aynadan değil
  // buradan beslenir (ayna 10 dk gecikmeli; bayat değeri geri yazmamak için).
  getOrderItemFields: async (docnum, itemnum, company, doctype) =>
    (await api.get(`/sales/orders/${encodeURIComponent(docnum)}/items/${itemnum}/fields`,
      { params: { company, doctype } })).data,

  // Kalem tedarikçisi seçicisi (teklif editörüyle aynı aday küme)
  getOrderItemVendors: async (company, search = '', material = '') =>
    (await api.get('/sales/orders/meta/vendors',
      { params: { company, search, material } })).data,

  updateOrderItem: async (docnum, itemnum, company, doctype, payload) =>
    (await api.patch(`/sales/orders/${encodeURIComponent(docnum)}/items/${itemnum}`,
      payload, { params: { company, doctype } })).data,

  setOrderGoods: async (docnum, itemnum, company, doctype, onDoc) =>
    (await api.patch(`/sales/orders/${encodeURIComponent(docnum)}/items/${itemnum}/goods`,
      { on_doc: !!onDoc }, { params: { company, doctype } })).data,

  // Türkçe mal tanımı (serbest bölge) — canias MTEXT + türeyen irsaliye/fatura
  // Serbest bölge (ihraç kayıtlı) — canias'a işler: başlık KDV '00', kalem KDV %0, menşei, metin 4
  // Faturalandırılacak para birimi (elle) — VERTEX + canias IRCCURRENCY; boş = seçimi kaldır
  setOrderInvoiceCurrency: async (docnum, company, doctype, currency, note) =>
    (await api.put(`/sales/orders/${encodeURIComponent(docnum)}/invoice-currency`,
      { currency: currency || null, note }, { params: { company, doctype } })).data,
  // Kur Çevrim Metodu — 0 Alış / 1 Satış (canias siparişi + faturası kesilmemiş irsaliyeler)
  setOrderExchMethod: async (docnum, company, doctype, method, note) =>
    (await api.put(`/sales/orders/${encodeURIComponent(docnum)}/exch-method`,
      { method, note }, { params: { company, doctype } })).data,
  setOrderFreeZone: async (docnum, company, doctype, enabled, note) =>
    (await api.put(`/sales/orders/${encodeURIComponent(docnum)}/free-zone`,
      { enabled: !!enabled, note }, { params: { company, doctype } })).data,
  setOrderGoodsText: async (docnum, itemnum, company, doctype, text) =>
    (await api.put(`/sales/orders/${encodeURIComponent(docnum)}/items/${itemnum}/goods-text`,
      { text }, { params: { company, doctype } })).data,

  deleteOrderItem: async (docnum, itemnum, company, doctype, note = '') =>
    (await api.delete(`/sales/orders/${encodeURIComponent(docnum)}/items/${itemnum}`,
      { params: { company, doctype, note } })).data,

  // Belgeye işlem yapılmış mı (irsaliye/fatura/satınalma/üretim/rezervasyon)?
  // Silme düğmesi bunun cevabına göre çıkar — kural sunucuda TEK yerde.
  orderDeleteCheck: async (docnum, company, doctype) =>
    (await api.get(`/sales/orders/${encodeURIComponent(docnum)}/delete-check`,
      { params: { company, doctype } })).data,

  deleteOrderDocument: async (docnum, company, doctype, note = '') =>
    (await api.delete(`/sales/orders/${encodeURIComponent(docnum)}`,
      { params: { company, doctype }, data: { note } })).data,

  restoreOrderDocument: async (docnum, company, doctype, note = '') =>
    (await api.post(`/sales/orders/${encodeURIComponent(docnum)}/restore`,
      { note }, { params: { company, doctype } })).data,
}
